// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import achievementManager from '../utils/system/AchievementManager';
import { ACHIEVEMENT_CATEGORIES, getAchievementsByCategory } from '../config/AchievementsConfig';
import './AchievementsModal.css';

/**
 * Achievements Modal Component
 * 
 * Displays all achievements with progress tracking
 */
const AchievementsModal = ({ show, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState(ACHIEVEMENT_CATEGORIES.EXPLORATION);
  const [progress, setProgress] = useState(null);
  const [achievements, setAchievements] = useState([]);

  useEffect(() => {
    if (show) {
      updateProgress();
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      const categoryAchievements = getAchievementsByCategory(selectedCategory);
      setAchievements(categoryAchievements);
    }
  }, [selectedCategory, show]);

  const updateProgress = () => {
    const prog = achievementManager.getProgress();
    setProgress(prog);
  };

  const handleClose = () => {
    onClose();
  };

  if (!show || !progress) return null;

  const categories = [
    { id: ACHIEVEMENT_CATEGORIES.EXPLORATION, name: 'Exploration', icon: '🌟' },
    { id: ACHIEVEMENT_CATEGORIES.COMBAT, name: 'Combat', icon: '⚔️' },
    { id: ACHIEVEMENT_CATEGORIES.TRADING, name: 'Trading', icon: '💰' },
    { id: ACHIEVEMENT_CATEGORIES.PROGRESSION, name: 'Progression', icon: '⚙️' },
    { id: ACHIEVEMENT_CATEGORIES.STORY, name: 'Story', icon: '🌀' },
    { id: ACHIEVEMENT_CATEGORIES.MASTERY, name: 'Mastery', icon: '🏆' }
  ];

  return createPortal(
    <div className="achievements-modal-overlay" onClick={handleClose}>
      <div className="achievements-modal" onClick={(e) => e.stopPropagation()}>
        <div className="achievements-modal-header">
          <h2>🏆 ACHIEVEMENTS</h2>
          <button className="achievements-modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="achievements-progress-summary">
          <div className="achievements-progress-bar-container">
            <div className="achievements-progress-bar">
              <div 
                className="achievements-progress-fill" 
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <div className="achievements-progress-text">
              {progress.unlocked} / {progress.total} ({progress.percentage}%)
            </div>
          </div>
          <div className="achievements-points">
            {progress.earnedPoints} / {progress.totalPoints} points
          </div>
        </div>

        <div className="achievements-categories">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`achievements-category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span className="achievements-category-icon">{cat.icon}</span>
              <span className="achievements-category-name">{cat.name}</span>
            </button>
          ))}
        </div>

        <div className="achievements-list">
          {achievements.map(achievement => {
            const isUnlocked = achievementManager.isUnlocked(achievement.id);
            const progress = achievementManager.getAchievementProgress(achievement.id);
            
            return (
              <div 
                key={achievement.id} 
                className={`achievement-item ${isUnlocked ? 'unlocked' : 'locked'} ${achievement.hidden && !isUnlocked ? 'hidden' : ''}`}
              >
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-details">
                  <div className="achievement-name">
                    {achievement.hidden && !isUnlocked ? '???' : achievement.name}
                    {isUnlocked && <span className="achievement-unlocked-badge">✓</span>}
                  </div>
                  <div className="achievement-description">
                    {achievement.hidden && !isUnlocked ? 'Hidden achievement' : achievement.description}
                  </div>
                  {!isUnlocked && progress && !achievement.hidden && (
                    <div className="achievement-progress">
                      <div className="achievement-progress-bar">
                        <div 
                          className="achievement-progress-fill" 
                          style={{ width: `${progress.percentage}%` }}
                        />
                      </div>
                      <div className="achievement-progress-text">
                        {progress.current} / {progress.required}
                      </div>
                    </div>
                  )}
                  <div className="achievement-points">{achievement.points} points</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AchievementsModal;
